/**
 * Shared Database Operations
 *
 * This module provides common IndexedDB operations that can be used
 * by both the main application and the service worker.
 */

import { DB_CONFIG } from "../../config";

// Open IndexedDB
export const openDB = async (
  dbName = DB_CONFIG.name,
  version = DB_CONFIG.version
) => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(dbName, version);

    request.onerror = (event) => {
      console.error("IndexedDB error:", event.target.error);
      reject(event.target.error);
    };

    request.onsuccess = (event) => {
      resolve(event.target.result);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      const oldVersion = event.oldVersion;

      console.log(`Database upgrade from version ${oldVersion} to ${version}`);

      // Create stores if they don't exist
      if (!db.objectStoreNames.contains("syncQueue")) {
        const syncStore = db.createObjectStore("syncQueue", {
          keyPath: "id",
          autoIncrement: true,
        });
        syncStore.createIndex("timestamp", "timestamp");
        console.log("Created syncQueue store");
      }

      if (!db.objectStoreNames.contains("analytics")) {
        const analyticsStore = db.createObjectStore("analytics", {
          keyPath: "id",
          autoIncrement: true,
        });
        analyticsStore.createIndex("timestamp", "timestamp");
        analyticsStore.createIndex("eventType", "eventType");
        console.log("Created analytics store");
      }

      if (!db.objectStoreNames.contains("rows")) {
        db.createObjectStore("rows", { keyPath: "id" });
        console.log("Created rows store");
      }

      // Create auth store for authentication status
      if (!db.objectStoreNames.contains("auth")) {
        db.createObjectStore("auth", { keyPath: "id" });
        console.log("Created auth store");
      }

      // Create categories store for customizable categories
      if (!db.objectStoreNames.contains("categories")) {
        db.createObjectStore("categories", { keyPath: "id" });
        console.log("Created categories store");
      }
    };
  });
};

// Function to get items from the sync queue
export const getSyncQueue = async (limit = 0) => {
  try {
    const db = await openDB();
    const tx = db.transaction("syncQueue", "readonly");
    const store = tx.objectStore("syncQueue");
    const index = store.index("timestamp");

    return new Promise((resolve, reject) => {
      const items = [];

      if (limit <= 0) {
        // Get all items
        index.openCursor().onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            items.push(cursor.value);
            cursor.continue();
          } else {
            resolve(items);
          }
        };
      } else {
        // Get limited items
        let count = 0;
        index.openCursor().onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor && count < limit) {
            items.push(cursor.value);
            count++;
            cursor.continue();
          } else {
            resolve(items);
          }
        };
      }

      tx.onerror = (event) => {
        console.error("Error getting sync queue:", event.target.error);
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error("Error getting sync queue:", error);
    throw error;
  }
};

// Function to remove an item from the sync queue
export const removeFromSyncQueue = async (id) => {
  try {
    const db = await openDB();
    const tx = db.transaction("syncQueue", "readwrite");
    const store = tx.objectStore("syncQueue");

    return new Promise((resolve, reject) => {
      const request = store.delete(id);

      request.onsuccess = () => resolve(true);

      tx.onerror = (event) => {
        console.error("Error removing from sync queue:", event.target.error);
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error("Error removing from sync queue:", error);
    throw error;
  }
};

// Function to update an item in the sync queue
export const updateSyncQueueItem = async (id, updates) => {
  try {
    const db = await openDB();
    const tx = db.transaction("syncQueue", "readwrite");
    const store = tx.objectStore("syncQueue");

    return new Promise((resolve, reject) => {
      const getRequest = store.get(id);

      getRequest.onsuccess = () => {
        const item = getRequest.result;
        if (!item) {
          reject(new Error(`Sync queue item with id ${id} not found`));
          return;
        }

        const updatedItem = { ...item, ...updates };
        const putRequest = store.put(updatedItem);

        putRequest.onsuccess = () => resolve(updatedItem);
      };

      tx.onerror = (event) => {
        console.error("Error updating sync queue item:", event.target.error);
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error("Error updating sync queue item:", error);
    throw error;
  }
};

// Add a categorization to the sync queue
export const addToSyncQueue = async (categorization) => {
  try {
    const db = await openDB();

    const item = {
      ...categorization,
      timestamp: new Date().toISOString(),
      status: "pending",
      retryCount: 0,
    };

    // Add the item to the sync queue
    const tx = db.transaction("syncQueue", "readwrite");
    const store = tx.objectStore("syncQueue");

    // Clear the no eligible items flag if it exists
    store.delete("noEligibleItemsFlag").catch(() => {
      // Ignore errors if the flag doesn't exist
    });

    return new Promise((resolve, reject) => {
      const request = store.add(item);

      request.onsuccess = (event) => {
        resolve(event.target.result); // Return the generated ID
      };

      tx.onerror = (event) => {
        console.error("Error adding to sync queue:", event.target.error);
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error("Error adding to sync queue:", error);
    throw error;
  }
};

// Log analytics data
export const logAnalytics = async (data) => {
  try {
    const db = await openDB();
    const tx = db.transaction("analytics", "readwrite");
    const store = tx.objectStore("analytics");

    const analyticsData = {
      ...data,
      timestamp: data.timestamp || new Date().toISOString(),
      isOnline:
        typeof navigator !== "undefined"
          ? navigator.onLine
          : typeof self !== "undefined" && self.navigator
          ? self.navigator.onLine
          : false,
      source: typeof window !== "undefined" ? "app" : "service-worker",
    };

    return new Promise((resolve, reject) => {
      const request = store.add(analyticsData);

      request.onsuccess = () => resolve(true);

      tx.onerror = (event) => {
        console.error("Error logging analytics:", event.target.error);
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error("Error logging analytics:", error);
    // Don't throw here to prevent disrupting main operations
    return false;
  }
};

// Get authentication status
export const getAuthStatus = async () => {
  try {
    const db = await openDB();

    const tx = db.transaction("auth", "readonly");
    const store = tx.objectStore("auth");

    return new Promise((resolve, reject) => {
      const request = store.get("authStatus");

      request.onsuccess = (event) => {
        const result = event.target.result;
        resolve(result ? result.isAuthenticated : false);
      };

      tx.onerror = (event) => {
        console.error("Error getting auth status:", event.target.error);
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error("Error getting auth status:", error);
    return false;
  }
};

// Set authentication status
export const setAuthStatus = async (isAuthenticated) => {
  try {
    const db = await openDB();
    const tx = db.transaction("auth", "readwrite");
    const store = tx.objectStore("auth");

    return new Promise((resolve, reject) => {
      const request = store.put({
        id: "authStatus",
        isAuthenticated,
        timestamp: new Date().toISOString(),
      });

      request.onsuccess = () => resolve(true);

      tx.onerror = (event) => {
        console.error("Error setting auth status:", event.target.error);
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error("Error setting auth status:", error);
    return false;
  }
};

// Store authentication token
export const storeAuthToken = async (tokenData) => {
  try {
    const db = await openDB();
    const tx = db.transaction("auth", "readwrite");
    const store = tx.objectStore("auth");

    // Ensure we have an expires_at field
    if (tokenData.expires_in && !tokenData.expires_at) {
      tokenData.expires_at = new Date(Date.now() + tokenData.expires_in * 1000).toISOString();
    }

    return new Promise((resolve, reject) => {
      const request = store.put({
        id: "authToken",
        ...tokenData,
        timestamp: new Date().toISOString(),
      });

      request.onsuccess = () => {
        // Also update the auth status
        setAuthStatus(true).catch(err => 
          console.error("Error updating auth status:", err)
        );
        resolve(true);
      };

      tx.onerror = (event) => {
        console.error("Error storing auth token:", event.target.error);
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error("Error storing auth token:", error);
    return false;
  }
};

// Get authentication token
export const getAuthToken = async () => {
  try {
    const db = await openDB();
    const tx = db.transaction("auth", "readonly");
    const store = tx.objectStore("auth");

    return new Promise((resolve, reject) => {
      const request = store.get("authToken");

      request.onsuccess = (event) => {
        const result = event.target.result;
        resolve(result || null);
      };

      tx.onerror = (event) => {
        console.error("Error getting auth token:", event.target.error);
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error("Error getting auth token:", error);
    return null;
  }
};

// Clear authentication token
export const clearAuthToken = async () => {
  try {
    const db = await openDB();
    const tx = db.transaction("auth", "readwrite");
    const store = tx.objectStore("auth");

    return new Promise((resolve, reject) => {
      const request = store.delete("authToken");

      request.onsuccess = () => {
        // Also update the auth status
        setAuthStatus(false).catch(err => 
          console.error("Error updating auth status:", err)
        );
        resolve(true);
      };

      tx.onerror = (event) => {
        console.error("Error clearing auth token:", event.target.error);
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error("Error clearing auth token:", error);
    return false;
  }
};

export default {
  openDB,
  getSyncQueue,
  removeFromSyncQueue,
  updateSyncQueueItem,
  addToSyncQueue,
  logAnalytics,
  getAuthStatus,
  setAuthStatus,
  storeAuthToken,
  getAuthToken,
  clearAuthToken,
};
